import { Map } from 'immutable'
import { getChaptersFromVTT } from './vtt-parser'

const { videojs } = global
const Component = videojs.getComponent('Component')
const MenuItem = videojs.getComponent('MenuItem')

/**
 * @class
 * @extends Component
 */
// eslint-disable-next-line import/prefer-default-export
export class VideoChaptersMenuChaptersColumn extends Component {
  /**
   * @param {Player|Object} player
   * @returns {void}
   */
  constructor (player, options) {
    super(player, { ...options, className: 'vjs-chapters-menu-chapters-column' })

    // Only add chapters list once
    if (!this.children().length) {
      this.#addChaptersList()
    }

    // Listen for language changes
    this.player().el().addEventListener('languageChanged', () => {
      // Use setTimeout to ensure the language change is fully processed
      setTimeout(() => {
        // Instead of clearing and rebuilding, just update the existing content
        this.#updateChaptersForNewLanguage()
      }, 0)
    })

    // Listen for time updates to highlight current chapter
    this.player().on('timeupdate', () => {
      this.updateCurrentChapterHighlight()
    })
  }

  /**
   * @returns {void}
   */
  #addChaptersList () {
    const plugin = this.player().VideoChapters()
    const chapters = plugin.getChapters()
    const currentLanguage = plugin.getCurrentLanguage()

    // Get the VTT URL for the current language
    let vttUrl = null
    if (Map.isMap(chapters)) {
      vttUrl = chapters.get(currentLanguage)
    } else if (chapters && typeof chapters === 'object') {
      vttUrl = chapters[currentLanguage]
    }

    if (!vttUrl) {
      const noChaptersItem = new MenuItem(this.player(), {
        text: this.player().VideoChapters().getStaticText('noChapters', 'No chapters available'),
        selectable: false,
      })
      this.addChild(noChaptersItem)
      return
    }

    // Add title
    const titleDiv = document.createElement('div')
    titleDiv.className = 'vjs-sub-menu-title'
    titleDiv.style.cssText = `
      font-weight: bold;
      color: #fff;
      padding: 8px 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    `
    titleDiv.textContent = 'Chapters'
    this.el().appendChild(titleDiv)

    // Show loading message
    const loadingItem = new MenuItem(this.player(), {
      text: 'Loading chapters...',
      selectable: false,
    })
    this.addChild(loadingItem)

    // First, try to get parsed chapters from the plugin
    // (which should be available if text tracks loaded)
    const parsedChapters = plugin.getParsedChapters()
    if (parsedChapters && parsedChapters.length > 0) {
      this.#renderChapters(parsedChapters, loadingItem)
      return
    }

    // If not available from plugin, try to get from text tracks
    const textTracks = this.player().textTracks()
    const chapterTrack = Array.from(textTracks).find((track) => track.kind === 'metadata'
      && track.label
      && track.label === `Chapters-${plugin.getLanguageLabel(currentLanguage)}`)

    if (chapterTrack && chapterTrack.cues && chapterTrack.cues.length > 0) {
      // Use text track cues
      const chaptersFromTrack = Array.from(chapterTrack.cues).map((cue) => ({
        time: cue.startTime,
        title: cue.text,
        startTime: cue.startTime,
        endTime: cue.endTime,
      }))

      this.#renderChapters(chaptersFromTrack, loadingItem)
    } else {
      // Wait a bit for the plugin to load chapters, then retry
      setTimeout(() => {
        this.#retryLoadChapters(loadingItem, vttUrl)
      }, 500)
    }
  }

  /**
   * Retry loading chapters with a delay
   * @param {Object} loadingItem - Loading menu item to remove
   * @param {string} vttUrl - VTT file URL
   * @param {number} retryCount - Number of retry attempts
   * @returns {void}
   */
  #retryLoadChapters (loadingItem, vttUrl, retryCount = 0) {
    const plugin = this.player().VideoChapters()
    const currentLanguage = plugin.getCurrentLanguage()

    // First, try to get parsed chapters from the plugin
    const parsedChapters = plugin.getParsedChapters()
    if (parsedChapters && parsedChapters.length > 0) {
      this.#renderChapters(parsedChapters, loadingItem)
      return
    }

    // If not available from plugin, try to get from text tracks
    const textTracks = this.player().textTracks()
    const chapterTrack = Array.from(textTracks).find((track) => track.kind === 'metadata'
      && track.label
      && track.label === `Chapters-${plugin.getLanguageLabel(currentLanguage)}`)

    if (chapterTrack && chapterTrack.cues && chapterTrack.cues.length > 0) {
      // Use text track cues
      const chaptersFromTrack = Array.from(chapterTrack.cues).map((cue) => ({
        time: cue.startTime,
        title: cue.text,
        startTime: cue.startTime,
        endTime: cue.endTime,
      }))

      this.#renderChapters(chaptersFromTrack, loadingItem)
    } else if (retryCount < 5) {
      // Retry again after a delay
      setTimeout(() => {
        this.#retryLoadChapters(loadingItem, vttUrl, retryCount + 1)
      }, 500)
    } else {
      // Final fallback to manual fetching
      this.#fetchChaptersManually(vttUrl, loadingItem)
    }
  }

  /**
   * Fetch chapters manually using API client
   * @param {string} vttUrl - VTT file URL
   * @param {Object} loadingItem - Loading menu item to remove
   * @returns {void}
   */
  #fetchChaptersManually (vttUrl, loadingItem) {
    getChaptersFromVTT(vttUrl, this.player().VideoChapters().getAuth())
      .then((parsedChapters) => {
        this.#renderChapters(parsedChapters, loadingItem)
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading chapters:', error)

        // Remove loading item
        this.removeChild(loadingItem)

        // Show error message
        const errorItem = new MenuItem(this.player(), {
          text: 'Error loading chapters',
          selectable: false,
        })
        this.addChild(errorItem)
      })
  }

  /**
   * Render chapters in the menu
   * @param {Array} parsedChapters - Array of chapter objects
   * @param {Object} loadingItem - Loading menu item to remove
   * @returns {void}
   */
  #renderChapters (parsedChapters, loadingItem) {
    // Remove loading item
    this.removeChild(loadingItem)

    if (parsedChapters.length === 0) {
      const noChaptersItem = new MenuItem(this.player(), {
        text: this.player().VideoChapters().getStaticText('noChapters', 'No chapters available'),
        selectable: false,
      })
      this.addChild(noChaptersItem)
      return
    }

    // Store parsed chapters in the plugin for highlighting
    this.player().VideoChapters().setParsedChapters(parsedChapters)

    // Add chapter items
    parsedChapters.forEach((chapter, index) => {
      const chapterDiv = document.createElement('div')
      chapterDiv.className = 'vjs-menu-item vjs-chapter-item'
      chapterDiv.setAttribute('data-chapter-time', chapter.time)
      chapterDiv.style.cssText = `
        color: #fff !important;
        background-color: transparent;
        padding: 8px 12px;
        cursor: pointer;
        border: none;
        text-align: left;
        width: 100%;
        font-size: 14px;
        line-height: 1.4;
      `
      chapterDiv.textContent = chapter.title || `Chapter ${index + 1}`

      chapterDiv.addEventListener('click', () => {
        this.player().currentTime(chapter.time)
        this.player().VideoChapters().closeMenu()
      })

      // Add the div to the component
      this.el().appendChild(chapterDiv)
    })

    // Initial highlight of current chapter
    this.updateCurrentChapterHighlight()
  }

  /**
   * Update chapters for the new language without recreating the plugin
   * @returns {void}
   */
  #updateChaptersForNewLanguage () {
    const plugin = this.player().VideoChapters()
    const chapters = plugin.getChapters()
    const currentLanguage = plugin.getCurrentLanguage()

    // Get the VTT URL for the current language
    const vttUrl = chapters.get(currentLanguage)

    if (!vttUrl) {
      return
    }

    // Clear existing chapter content (but keep the title)
    const titleDiv = this.el().querySelector('.vjs-sub-menu-title')
    if (titleDiv) {
      // Remove everything after the title
      let nextElement = titleDiv.nextElementSibling
      while (nextElement) {
        const toRemove = nextElement
        nextElement = nextElement.nextElementSibling
        toRemove.remove()
      }
    }

    // Show loading message
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'vjs-menu-item'
    loadingDiv.style.cssText = `
      color: #fff !important;
      background-color: transparent;
      padding: 8px 12px;
      cursor: default;
      border: none;
      text-align: left;
      width: 100%;
      font-size: 14px;
      line-height: 1.4;
    `
    loadingDiv.textContent = 'Loading chapters...'
    this.el().appendChild(loadingDiv)

    // Try to get chapters from Video.js text tracks first
    const textTracks = this.player().textTracks()
    const chapterTrack = Array.from(textTracks).find((track) => track.kind === 'metadata'
      && track.label
      && track.label.includes(plugin.getLanguageLabel(currentLanguage)))

    if (chapterTrack && chapterTrack.cues && chapterTrack.cues.length > 0) {
      // Use text track cues
      const parsedChapters = Array.from(chapterTrack.cues).map((cue) => ({
        time: cue.startTime,
        title: cue.text,
        startTime: cue.startTime,
        endTime: cue.endTime,
      }))

      this.#renderChaptersForNewLanguage(parsedChapters, loadingDiv)
    } else {
      // Fallback to manual fetching
      this.#fetchChaptersForNewLanguage(vttUrl, loadingDiv)
    }
  }

  /**
   * Fetch chapters for new language manually using API client
   * @param {string} vttUrl - VTT file URL
   * @param {Object} loadingDiv - Loading div to remove
   * @returns {void}
   */
  #fetchChaptersForNewLanguage (vttUrl, loadingDiv) {
    const chaptersPlugin = this.player().VideoChapters()
    getChaptersFromVTT(vttUrl, chaptersPlugin.getAuth())
      .then((parsedChapters) => {
        this.#renderChaptersForNewLanguage(parsedChapters, loadingDiv)
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading chapters for new language:', error)
        loadingDiv.remove()

        const errorDiv = document.createElement('div')
        errorDiv.className = 'vjs-menu-item'
        errorDiv.style.cssText = `
          color: #fff !important;
          background-color: transparent;
          padding: 8px 12px;
          cursor: default;
          border: none;
          text-align: left;
          width: 100%;
          font-size: 14px;
          line-height: 1.4;
        `
        errorDiv.textContent = 'Error loading chapters'
        this.el().appendChild(errorDiv)
      })
  }

  /**
   * Render chapters for new language in the menu
   * @param {Array} parsedChapters - Array of chapter objects
   * @param {Object} loadingDiv - Loading div to remove
   * @returns {void}
   */
  #renderChaptersForNewLanguage (parsedChapters, loadingDiv) {
    // Remove loading message
    loadingDiv.remove()

    if (parsedChapters.length === 0) {
      const noChaptersDiv = document.createElement('div')
      noChaptersDiv.className = 'vjs-menu-item'
      noChaptersDiv.style.cssText = `
        color: #fff !important;
        background-color: transparent;
        padding: 8px 12px;
        cursor: default;
        border: none;
        text-align: left;
        width: 100%;
        font-size: 14px;
        line-height: 1.4;
      `
      noChaptersDiv.textContent = 'No chapters available'
      this.el().appendChild(noChaptersDiv)
      return
    }

    // Store parsed chapters in the plugin for highlighting
    this.player().VideoChapters().setParsedChapters(parsedChapters)

    // Add chapter items
    parsedChapters.forEach((chapter, index) => {
      const chapterDiv = document.createElement('div')
      chapterDiv.className = 'vjs-menu-item vjs-chapter-item'
      chapterDiv.setAttribute('data-chapter-time', chapter.time)
      chapterDiv.style.cssText = `
        color: #fff !important;
        background-color: transparent;
        padding: 8px 12px;
        cursor: pointer;
        border: none;
        text-align: left;
        width: 100%;
        font-size: 14px;
        line-height: 1.4;
      `
      chapterDiv.textContent = chapter.title || `Chapter ${index + 1}`

      chapterDiv.addEventListener('click', () => {
        this.player().currentTime(chapter.time)
        this.player().VideoChapters().closeMenu()
      })

      this.el().appendChild(chapterDiv)
    })

    // Initial highlight of current chapter
    this.updateCurrentChapterHighlight()
  }

  /**
   * Update the current chapter highlighting
   * @returns {void}
   */
  updateCurrentChapterHighlight () {
    const plugin = this.player().VideoChapters()
    const currentChapter = plugin.getCurrentChapter()

    // Remove current highlighting from all chapter items
    const chapterItems = this.el().querySelectorAll('.vjs-chapter-item')
    chapterItems.forEach((item) => {
      item.classList.remove('vjs-current-chapter')
    })

    // Add highlighting to the current chapter
    if (currentChapter) {
      const currentChapterItem = this.el().querySelector(`[data-chapter-time="${currentChapter.time}"]`)
      if (currentChapterItem) {
        currentChapterItem.classList.add('vjs-current-chapter')
      }
    }
  }
}
